/**
 * O CN de um nome de certificado escrito como RFC 4514 — a forma que o executor publica
 * no emissor do bundle do SPIRE (`rfc4514_string` do `cryptography`, em Python).
 *
 * Por que a tela precisa disto (decisão de 04/10, aba SPIFFE/SPIRE de T5): o nome real
 * do lab começa com o número de série da CA pelo OID —
 * `2.5.4.5=62246480374612443686474954082216341856,CN=Segura AIM SPIRE CA,O=Segura,C=ARPA`
 * —, um texto longo e opaco antes do que interessa. A tela mostra só o CN, e o nome
 * inteiro fica no `title`.
 *
 * O que a leitura respeita, porque um `split(',')` erraria em silêncio:
 *
 * - vírgula, `+` e `;` ESCAPADOS (`CN=Acme\, Inc.`) não separam atributos;
 * - o CN pode vir pelo nome (`CN`, sem distinção de caixa) ou pelo OID (`2.5.4.3`);
 * - `\XX` em hexadecimal são bytes UTF-8 (`\C3\A7` é «ç»);
 * - com mais de um CN vale o primeiro: na RFC 4514 o atributo mais específico vem antes.
 *
 * Sem CN legível devolve `null`, e quem chama mostra o nome inteiro — o emissor de um
 * mock antigo (`spiffe://…`) não é nome de certificado, e cortar um texto que não se
 * entende esconderia a única informação que há.
 */
export function commonName(dn: string): string | null {
  for (const attribute of attributesOf(dn)) {
    const equals = attribute.indexOf('=');
    if (equals < 0) continue;
    const type = attribute.slice(0, equals).trim().toLowerCase();
    if (type !== 'cn' && type !== '2.5.4.3') continue;
    const value = attribute.slice(equals + 1).trim();
    // `#…` é o valor em BER, não texto: não há CN para mostrar.
    if (!value || value.startsWith('#')) return null;
    return unescapeValue(value) || null;
  }
  return null;
}

/** Os atributos do nome, partidos nos separadores NÃO escapados. */
function attributesOf(dn: string): string[] {
  const attributes: string[] = [];
  let current = '';
  for (let i = 0; i < dn.length; i++) {
    const char = dn[i];
    if (char === '\\' && i + 1 < dn.length) {
      current += char + dn[i + 1];
      i++;
      continue;
    }
    if (char === ',' || char === '+' || char === ';') {
      attributes.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  attributes.push(current);
  return attributes;
}

const HEX_PAIR = /^[0-9a-fA-F]{2}$/;

/** Desfaz os escapes de um valor: `\,` → `,` e `\XX` → o byte, lido como UTF-8. */
function unescapeValue(value: string): string {
  const decoder = new TextDecoder();
  const bytes: number[] = [];
  let text = '';
  const flush = () => {
    if (bytes.length === 0) return;
    text += decoder.decode(new Uint8Array(bytes));
    bytes.length = 0;
  };

  for (let i = 0; i < value.length; i++) {
    const char = value[i];
    const pair = value.slice(i + 1, i + 3);
    if (char === '\\' && HEX_PAIR.test(pair)) {
      bytes.push(parseInt(pair, 16));
      i += 2;
      continue;
    }
    flush();
    if (char === '\\' && i + 1 < value.length) {
      text += value[i + 1];
      i++;
      continue;
    }
    text += char;
  }
  flush();
  return text;
}
